import {
    Component,
    ViewChild,
    Output,
    EventEmitter,
    Input,
    input,
    effect,
    AfterViewInit,
    ElementRef,
    OnDestroy
} from '@angular/core';
import { jqxDateTimeInputComponent } from 'jqwidgets-ng/jqxdatetimeinput';

@Component({
    selector: 'JQDatePicker',
    templateUrl: `datePicker.component.html`
})
export class JqDatePicker implements AfterViewInit, OnDestroy {
    @Input() disabled: boolean;
    @Input() id: string;
    @Input() label: string;
    @Output() elementClicked: EventEmitter<any> = new EventEmitter<any>();
    @ViewChild('myDateTimeInput', { static: true })
    myDateTimeInput!: jqxDateTimeInputComponent;
    selectedDate = input<Date | null>(null);
    private liveRegion: HTMLElement | null = null;
    
    constructor(private host: ElementRef) {
        effect(() => {
            this.myDateTimeInput?.setDate(this.selectedDate());
        });
    }

    ngAfterViewInit(): void {
        this.myDateTimeInput.setDate(this.selectedDate());
        setTimeout(() => {
            this.fixAccessibility();
            this.attachFocusHandler();
            this.fixLabelLinkage();
            this.hideEmptyJqxLabel();
        }, 0);
    }

    ngOnDestroy(): void {        
        if (this.liveRegion && this.liveRegion.parentNode) {
            this.liveRegion.parentNode.removeChild(this.liveRegion);
        }
    }

    private fixAccessibility() {
        const widgetRoot: HTMLElement = this.myDateTimeInput.host?.[0];
        if (!widgetRoot) return;

        const inputEl = widgetRoot.querySelector('input');

        
        widgetRoot.removeAttribute('role');
        widgetRoot.removeAttribute('aria-roledescription');
        widgetRoot.removeAttribute('aria-valuetext');
        widgetRoot.removeAttribute('aria-label');
        widgetRoot.removeAttribute('data-role');

        
        if (!this.liveRegion) {
            this.liveRegion = document.createElement('div');
            this.liveRegion.setAttribute('aria-live', 'polite');
            this.liveRegion.setAttribute('aria-atomic', 'true');
            this.liveRegion.className = 'sr-only';
            this.liveRegion.style.position = 'absolute';
            this.liveRegion.style.left = '-10000px';
            this.liveRegion.style.width = '1px';
            this.liveRegion.style.height = '1px';
            this.liveRegion.style.overflow = 'hidden';
            document.body.appendChild(this.liveRegion);
        }

        if (inputEl) {
            //inputEl.setAttribute('aria-labelledby', `${this.id}_label`);
            inputEl.setAttribute('type', 'text');
            inputEl.setAttribute('role', 'combobox'); 
            inputEl.setAttribute('aria-expanded', 'false');
            inputEl.setAttribute('aria-haspopup', 'dialog');            
            inputEl.removeAttribute('aria-live');
            inputEl.removeAttribute('aria-atomic');

            this.updateAriaValueText(inputEl);
        }

        
        const jqxLiveRegion = widgetRoot.querySelector('.jqx-date-time-picker-live-area');
        if (jqxLiveRegion) jqxLiveRegion.remove();
        const span = widgetRoot.querySelector('.jqx-input-bar');
        const label = widgetRoot.querySelector('.jqx-input-label');

        if (span) span.remove();
        if (label) label.remove();
    }

    private updateAriaValueText(inputEl: HTMLElement) {
        const date = this.myDateTimeInput.getDate();

        if (!date) {
            inputEl.setAttribute('aria-valuetext', 'No date selected');
            if (this.liveRegion) {
                this.liveRegion.textContent = ''; 
            }
            return;
        }

        const formatted = date.toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });

        inputEl.setAttribute('aria-valuetext', `Selected date is ${formatted}`);

        
        if (this.liveRegion) {
            
            this.liveRegion.textContent = '';
            setTimeout(() => {
                if (this.liveRegion) {
                    this.liveRegion.textContent = `Selected date is ${formatted}`;
                    inputEl.setAttribute('aria-expanded', 'false');
                }
            }, 100);
        }
    }
    private attachFocusHandler() {
        const widgetRoot: HTMLElement = this.myDateTimeInput.host?.[0];
        if (!widgetRoot) return;

        const inputEl = widgetRoot.querySelector('input') as HTMLInputElement;
        if (inputEl) {
            inputEl.addEventListener('focus', () => {
                
                setTimeout(() => {
                    inputEl.setSelectionRange(0, inputEl.value.length);
                }, 0);
            });
        }
    }
    onValueChanged(event: any) {
        const date = event.args.date;
        if (date) this.elementClicked.emit(date);

        // Update ARIA when value changes
        const widgetRoot = this.myDateTimeInput.host?.[0];
        const inputEl = widgetRoot?.querySelector('input');
        if (inputEl) {
            this.updateAriaValueText(inputEl);
        }
    }

    onKeyDown(event: KeyboardEvent) {
        const widgetRoot = this.myDateTimeInput.host?.[0];
        const inputEl = widgetRoot?.querySelector('input');

        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();
            if (!this.disabled) {
                this.myDateTimeInput.open();
                if (inputEl) {
                    inputEl.setAttribute('aria-expanded', 'true');
                }
            }
        }
        if (event.key === 'Escape') {
            this.myDateTimeInput.close();
            if (inputEl) {
                inputEl.setAttribute('aria-expanded', 'false');
            }
        }
    }
    private fixLabelLinkage(): void {
        const hostEle = this.host.nativeElement;
        console.log('forId:', this.id);
        const jqxInput = hostEle.querySelector('input[role="combobox"]');
        const labelEl = hostEle.querySelector(`label[for='${this.id}`);
        console.log('jqxInput found:', jqxInput); 
        console.log('labelEl found:', labelEl); 

        if (jqxInput && labelEl) {
            const realId = `${this.id}_input`;
            jqxInput.setAttribute('id', realId);
            labelEl.setAttribute('for', realId);
            //jqxInput.setAttribute('aria-labelledby', `${this.id}_label`);
        }
    }

    private hideEmptyJqxLabel(): void {
        const host = this.host.nativeElement;
        const emptyLabel = host.querySelector('label.jqx-input-label');

        if (emptyLabel) {
            emptyLabel.setAttribute('aria-hidden', 'true');
        }
    }
}